import React, { useState } from 'react'
import { cloneDeep } from 'lodash'
import { Row, Col, Badge, Space, Image } from 'antd'
import { CreateReq, ProductOptions as ProductOptionsTypes } from 'Store/product/types'
import { TableListPage } from 'Components/common/Table'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { StatusTag } from 'Components/utilities'
import { ActionButton, PrimaryButton } from 'Components/button'
import { CopyOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { appConfig, themeConfig } from 'config'
import ProductOptionDrawer from './ProductOptionDrawer'
import SpecialProductOptionDrawer from './SpecialProductOptionDrawer'
import { FormItem } from 'Components/form'
import Label from 'Components/text/Label'
import { numberWithComma, roundNumber } from 'helpers/format'
import { ImageFallback } from 'helpers/constanst'
import RootState from 'typings/RootState'
import { selectDatas } from 'Store/config/selector'
import { connect } from 'react-redux'
import LinkText from 'Components/text/LinkText'

type Props = {
  formData: CreateReq
  onUpdateData: (val: any, key: string) => void
  isError: (name: string) => boolean
  isRequired: (name: string) => boolean
  token?: string
  isDisabled: (name: string) => boolean
  isPackageC?: boolean
  uom?: string
} & ReduxProps

const ProductOptions: React.FunctionComponent<Props> = props => {
  const { formData, onUpdateData, isError, token, isDisabled, isPackageC = false, configs, uom = '' } = props
  const priceLabel = configs?.value.price_labels || []
  const enableProductOnline = themeConfig?.component?.elementOnlineProduct

  const [selectedData, setSelectedData] = useState(undefined as ProductOptionsTypes | undefined)
  const [drawer, setDrawer] = useState(false)

  const onOpenDrawer = () => {
    setDrawer(true)
  }

  const onClose = () => {
    setDrawer(false)
  }

  const onClickAdd = () => {
    const isFirst = formData?.product_options && formData.product_options.length <= 0
    if (isFirst) {
      setSelectedData({ title: formData.title, publish: true, photo: formData?.photos[0] || '' } as any)
      onOpenDrawer()
    } else {
      setSelectedData(undefined)
      onOpenDrawer()
    }
  }

  const onClickEdit = (data: ProductOptionsTypes) => {
    setSelectedData(data)
    onOpenDrawer()
  }

  const mockId = () => {
    const random = Math.floor(Math.random() * 100)
    const length = (formData?.product_options && formData?.product_options.length) || 0
    return `new-${length + 1}-${random}`
  }

  const onClickClone = (data: ProductOptionsTypes) => {
    onAddProductOption({ ...data, quantity_offline: 0, quantity: 0 })
  }

  const onClickSave = (data: ProductOptionsTypes) => {
    if (data?.id) {
      onEditProductOption(data)
    } else {
      onAddProductOption(data)
    }
    onClose()
  }

  const getConfigName = (key: string): string => {
    const result = priceLabel && priceLabel.find(item => item.name === key)
    return `${result?.value || ''} ${result?.description ? '(' + result?.description + ')' : ''}`
  }

  const onAddProductOption = (data: ProductOptionsTypes) => {
    console.log('data', data)
    const product_options = cloneDeep(formData?.product_options || [])
    const newItem = cloneDeep(data) as ProductOptionsTypes
    newItem.id = mockId()
    product_options.push(newItem)
    onUpdateData(product_options, 'product_options')
  }

  const onEditProductOption = (data: ProductOptionsTypes) => {
    const product_options = cloneDeep(formData.product_options)
    const result = product_options.map((item: ProductOptionsTypes) => {
      if (item?.id === data.id) {
        return data
      } else {
        return item
      }
    })
    onUpdateData(result, 'product_options')
  }

  const onRemoveProductOption = (id: any) => {
    let product_options = cloneDeep(formData.product_options)
    product_options = product_options.filter((item: any) => item?.id !== id)
    onUpdateData(product_options, 'product_options')
  }

  const onAddProductImage = (file: string[]) => {
    onUpdateData(file, 'photos')
  }

  const onUpdateProductOptionImages = (id: number) => {
    const product_options = cloneDeep(formData.product_options)
    const result = product_options.map((item: ProductOptionsTypes) => {
      if (item?.id === id) {
        item.photo = formData?.photos ? formData?.photos[0] : ''
      }
      return item
    })
    onUpdateData(result, 'product_options')
  }

  const renderImage = (img: string, record: ProductOptionsTypes) => {
    return (
      <Space direction="vertical">
        <ThumbImage src={appConfig.assetCdnEndpoint + img} fallback={ImageFallback} />
        {record.photo !== (formData?.photos && formData?.photos[0]) && (
          <Link onClick={() => onUpdateProductOptionImages(record?.id || 0)}>เปลี่ยนรูปภาพ</Link>
        )}
      </Space>
    )
  }

  const renderStatus = (publish: boolean) => (
    <StatusTag isPass={publish === true} text={publish ? 'เปิดใช้งาน' : 'ปิดใช้งาน'} />
  )

  const renderActionButton = (key: number, record: ProductOptionsTypes) => {
    return (
      <Space size="middle">
        <ActionButton
          text=""
          icon={<CopyOutlined />}
          label="Clone สินค้า"
          action="success"
          onClick={() => onClickClone(record)}
          disabled={isDisabled('product_options_clone')}
        />
        <ActionButton
          text=""
          icon={<EditOutlined />}
          label="แก้ไข"
          action="edit"
          onClick={() => onClickEdit(record)}
          disabled={isDisabled('product_options_edit')}
        />
        <ActionButton
          text=""
          icon={<DeleteOutlined />}
          label="ลบ"
          action="deleted"
          onClick={() => onRemoveProductOption(record?.id || 0)}
          disabled={isDisabled('product_options_delete')}
        />
      </Space>
    )
  }

  const renderCount = (color: string, title: string, count: any, unit: string = 'ซม.') => (
    <DetailText key={title}>
      <Row justify="space-between" align="top" gutter={[8, 8]}>
        <Col>
          {color && <Badge color={color} />}
          {title}
        </Col>
        <Col>
          {count} {unit}
        </Col>
      </Row>
    </DetailText>
  )

  const renderDetail = (weight: number, record: ProductOptionsTypes) => {
    return (
      <>
        {record.weight > 0 && renderCount('grey', 'น้ำหนัก', roundNumber(weight), 'กก.')}
        {record.width > 0 ? renderCount('grey', 'ความกว้าง', roundNumber(record.width)) : ''}
        {record.height > 0 ? renderCount('grey', 'ความสูง', roundNumber(record.height)) : ''}
        {record.depth > 0 ? renderCount('grey', 'ความลึก', roundNumber(record.depth)) : ''}
        {record.length > 0 ? renderCount('grey', 'ความยาว', roundNumber(record.length)) : ''}
        {record.area > 0 ? renderCount('grey', 'พื้นที่', roundNumber(record.area)) : ''}
      </>
    )
  }

  const renderPrice = (weight: number, record: ProductOptionsTypes) => {
    return (
      <>
        {enableProductOnline &&
          renderCount('green', 'ราคาขายออนไลน์', numberWithComma(record?.price || 0, true), 'บาท')}
        {renderCount('orange', 'ราคาขายหน้าร้าน', numberWithComma(record?.offline_price || 0, true), 'บาท')}
        {(record?.price_a || 0) > 0 &&
          renderCount('grey', getConfigName('price_a'), numberWithComma(record?.price_a || 0, true), 'บาท')}
        {(record?.price_b || 0) > 0 &&
          renderCount('grey', getConfigName('price_b'), numberWithComma(record?.price_b || 0, true), 'บาท')}
        {(record?.price_c || 0) > 0 &&
          renderCount('grey', getConfigName('price_c'), numberWithComma(record?.price_c || 0, true), 'บาท')}
      </>
    )
  }

  const renderStock = (weight: number, record: ProductOptionsTypes) => {
    const stockCount = (record.quantity || 0) + (record.quantity_offline || 0)
    const stock = numberWithComma(stockCount)
    return `${stock} ${uom}`
  }

  const columns = [
    {
      title: 'รูปภาพ',
      dataIndex: 'photo',
      key: 'photo',
      render: renderImage,
      width: '120px',
    },
    {
      title: 'ชื่อสินค้า',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'ราคา',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => numberWithComma(price),
    },
    {
      title: 'สต๊อก',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity: number) => numberWithComma(quantity),
    },
    {
      title: 'รายละเอียด',
      dataIndex: 'weight',
      key: 'weight',
      render: renderDetail,
      width: '150px',
    },
    {
      title: 'สถานะ',
      dataIndex: 'publish',
      key: 'publish',
      render: renderStatus,
    },
    {
      title: 'Clone/แก้ไข/ลบ',
      dataIndex: 'key',
      key: 'key',
      render: renderActionButton,
    },
  ]

  const columnsPackageC = [
    {
      title: 'รูปภาพ',
      dataIndex: 'photo',
      key: 'photo',
      render: renderImage,
      width: '120px',
    },
    {
      title: 'ชื่อสินค้า',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: `ราคา/${uom || 'หน่วยสินค้าหลัก'}`,
      dataIndex: 'price',
      key: 'price',
      render: renderPrice,
      width: '250px',
    },
    {
      title: 'สต๊อก',
      dataIndex: 'quantity',
      key: 'quantity',
      render: renderStock,
    },
    enableProductOnline
      ? {
          title: 'เปอร์เซ็นต์ขายบนสต๊อกออนไลน์ (%)',
          dataIndex: 'online_percentage',
          key: 'online_percentage',
          render: (val: number) => `${val}%`,
        }
      : {
          title: '',
          dataIndex: 'online_percentage',
          key: 'online_percentage',
          width: '1px',
          render: () => '',
        },
    {
      title: 'รายละเอียด',
      dataIndex: 'weight',
      key: 'weight',
      render: renderDetail,
      width: '150px',
    },
    {
      title: 'สถานะ',
      dataIndex: 'publish',
      key: 'publish',
      render: renderStatus,
    },
    {
      title: 'Clone/แก้ไข/ลบ',
      dataIndex: 'key',
      key: 'key',
      render: renderActionButton,
    },
  ]

  const error = isError('product_options') || isError('product_price') || isError('product_options_status')
  let errorMsg = ''
  if (error) {
    if (isError('product_options')) {
      errorMsg = 'กรุณาระบุ ตัวเลือกสินค้าอย่างน้อย 1 ตัวเลือก'
    } else if (isError('product_price')) {
      errorMsg = 'กรุณาระบุ ราคาตัวเลือกสินค้ามากกว่า 0 บาท'
    } else if (isError('product_options_status')) {
      errorMsg = 'กรุณาเปลี่ยนสถานะตัวเลือกสินค้าเป็น "เปิดใช้งาน" อย่างน้อย 1 ตัวเลือก'
    }
  }

  return (
    <Row className="ttr-product-option">
      <Col md={24} xs={24}>
        <FormItem label="" status={error} error={errorMsg}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title>
                ตัวเลือกสินค้า*{' '}
                <LabelStyle>
                  (เพิ่มอย่างน้อย 1 รายการ และสามารถเพิ่มตัวเลือกได้หลายรายการ เมื่อสินค้าแตกต่างกันเช่น
                  สีและขนาด)
                </LabelStyle>
              </Title>
            </Col>
            <Col>
              <PrimaryButton
                btnType="Primary"
                text="เพิ่มตัวเลือกสินค้า"
                onClick={onClickAdd}
                icon={<PlusOutlined />}
                disabled={isDisabled('product_options_add')}
              />
            </Col>
          </Row>

          <Row>
            <Col md={24} xs={24}>
              <TableListPage
                columns={isPackageC ? columnsPackageC : columns}
                datas={formData.product_options}
                //scrollX={800}
                size={'small'}
                bordered={isPackageC ? true : false}
              />
            </Col>
          </Row>
        </FormItem>
      </Col>
      {isPackageC ? (
        <SpecialProductOptionDrawer
          data={selectedData}
          isShow={drawer}
          onClose={onClose}
          onSave={onClickSave}
          productInfo={formData}
          token={token}
          onAddProductImage={onAddProductImage}
          disableInput={isDisabled('product_options_detail')}
          uom={uom}
        />
      ) : (
        <ProductOptionDrawer
          data={selectedData}
          isShow={drawer}
          onClose={onClose}
          onSave={onClickSave}
          productInfo={formData}
          token={token}
          onAddProductImage={onAddProductImage}
          disableInput={isDisabled('product_options_detail')}
          uom={uom}
        />
      )}
    </Row>
  )
}

const Link: any = styled(LinkText)`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

const DetailText = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
  font-weight: 100;
  line-height: 1;
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.regular};
  padding-right: 10px;
`

const ThumbImage = styled(Image)`
  border-radius: 5px;
  width: 80px;
  height: 80px;
  object-fit: cover;
`

const Title = styled.div`
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.bold};
  font-weight: 500;
`

const LabelStyle: any = styled(Label)`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

const mapStateToProps = (state: RootState) => {
  return {
    configs: selectDatas(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(ProductOptions)
